import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, User, Phone, MapPin, Plus, ArrowLeft, Printer, Home } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { format, addDays, startOfToday, isBefore } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import Receipt from '@/components/Receipt';

const KasirBooking = () => {
  const navigate = useNavigate();
  const receiptRef = useRef(null);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [slots, setSlots] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [customerData, setCustomerData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: ''
  });
  const [paymentStatus, setPaymentStatus] = useState('cash'); // 'cash' or 'pending'
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastBooking, setLastBooking] = useState(null);
  const [lastPayment, setLastPayment] = useState(null);
  const [venuePricingRulesMap, setVenuePricingRulesMap] = useState({});
  const [hourlyPricingRules, setHourlyPricingRules] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/kasir/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'kasir') {
        toast.error('Akses ditolak');
        navigate('/kasir/login');
        return;
      }
    } catch (error) {
      navigate('/kasir/login');
      return;
    }
    
    loadVenues();
    loadPricingSettings();
  }, [navigate]);

  useEffect(() => {
    if (selectedVenue) {
      loadSlots();
    }
  }, [selectedDate, selectedVenue]);

  useEffect(() => {
    const rules = selectedVenue?.id ? venuePricingRulesMap?.[selectedVenue.id] : null;
    setHourlyPricingRules(Array.isArray(rules) ? rules : []);
  }, [selectedVenue, venuePricingRulesMap]);

  const loadVenues = async () => {
    try {
      const response = await api.get('/venues');
      setVenues(response.data);
      if (response.data.length > 0) {
        setSelectedVenue(response.data[0]);
      }
    } catch (error) {
      toast.error('Gagal memuat data lapangan');
    } finally {
      setLoading(false);
    }
  };

  const loadPricingSettings = async () => {
    try {
      const response = await api.get('/settings/public');
      const rawMap = response.data?.venue_hourly_pricing_rules;

      if (rawMap) {
        try {
          const parsed = JSON.parse(rawMap);
          setVenuePricingRulesMap(parsed && typeof parsed === 'object' ? parsed : {});
        } catch {
          setVenuePricingRulesMap({});
        }
      } else {
        setVenuePricingRulesMap({});
      }
    } catch {
      setVenuePricingRulesMap({});
    }
  };

  const getDayType = (dateStr) => {
    const dt = dateStr ? new Date(`${dateStr}T00:00:00`) : null;
    if (!dt || Number.isNaN(dt.getTime())) return 'weekday';
    const day = dt.getDay();
    return day === 0 || day === 6 ? 'weekend' : 'weekday';
  };

  const getHourlyRate = (hour, dayType) => {
    const rules = Array.isArray(hourlyPricingRules) ? hourlyPricingRules : [];
    const day = String(dayType || 'all').toLowerCase();

    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour <= 0) continue;
      if (ruleDayType === day && hour >= startHour && hour < endHour) return pricePerHour;
    }

    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour <= 0) continue;
      if (ruleDayType === 'all' && hour >= startHour && hour < endHour) return pricePerHour;
    }

    return null;
  };

  const calculateTotalAmount = (timeSlots, bookingDate) => {
    const slotsArr = Array.isArray(timeSlots) ? timeSlots : [];
    if (slotsArr.length === 0) return 0;
    const dayType = getDayType(bookingDate);
    let total = 0;
    for (const slot of slotsArr) {
      const match = typeof slot === 'string' ? slot.match(/^(\d{1,2}):/) : null;
      const hour = match ? Number(match[1]) : null;
      if (!Number.isInteger(hour)) return null;
      const rate = getHourlyRate(hour, dayType);
      if (!rate) return null;
      total += rate;
    }
    return total;
  };

  const loadSlots = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/bookings/slots?venue_id=${selectedVenue.id}&date=${dateStr}`);
      setSlots(response.data);
    } catch (error) {
      toast.error('Gagal memuat slot waktu');
    }
  };

  const handleBooking = async () => {
    if (!selectedVenue) {
      toast.error('Pilih lapangan terlebih dahulu');
      return;
    }

    if (selectedSlots.length === 0) {
      toast.error('Pilih minimal 1 slot waktu');
      return;
    }

    if (!customerData.customer_name || !customerData.customer_phone) {
      toast.error('Nama dan nomor HP customer wajib diisi');
      return;
    }

    const bookingDateStr = format(selectedDate, 'yyyy-MM-dd');
    const totalAmount = calculateTotalAmount(selectedSlots, bookingDateStr);
    if (totalAmount == null || totalAmount <= 0) {
      toast.error('Harga belum diatur untuk jam yang dipilih. Atur di admin lapangan.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/bookings', {
        venue_id: selectedVenue.id,
        booking_date: bookingDateStr,
        time_slots: selectedSlots,
        ...customerData
      });

      let paymentData = null;

      // If cash payment, create payment and mark as verified
      if (paymentStatus === 'cash') {
        const paymentResponse = await api.post('/payments', {
          booking_id: response.data.id,
          method: 'cash',
          amount: totalAmount
        });

        // Auto verify for cash payment
        const user = JSON.parse(localStorage.getItem('user'));
        await api.put(`/admin/payments/${paymentResponse.data.id}/verify`, {
          status: 'verified',
          admin_id: user.id
        });

        paymentData = { ...paymentResponse.data, status: 'verified' };
        toast.success('Booking berhasil dibuat & dibayar tunai!');
      } else {
        toast.success('Booking berhasil dibuat! Status: Menunggu pembayaran');
      }

      // Store booking data for receipt
      setLastBooking({
        ...response.data,
        time_slots: selectedSlots,
        booking_date: bookingDateStr
      });
      setLastPayment(paymentData);
      setShowReceipt(true);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking gagal');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup diblokir. Izinkan popup untuk print struk.');
      return;
    }

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Struk Booking - Zero Futsal</title>
    ${styles}
    <style>
      @page { size: 58mm auto; margin: 0; }
      html, body { width: 58mm; margin: 0; padding: 0; background: #fff; }
      body { margin: 0; padding: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      [data-receipt-root] { width: 58mm !important; box-sizing: border-box !important; padding: 4mm 3mm !important; }
      [data-receipt-root], [data-receipt-root] * { color: #000 !important; }
      [data-receipt-root] { font-family: monospace !important; }
    </style>
  </head>
  <body>
    ${printContent.outerHTML}
    <script>
      window.onload = function () {
        window.focus();
        setTimeout(function () {
          window.print();
        }, 400);
      }
      window.onafterprint = function () {
        window.close();
      }
    </script>
  </body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setLastBooking(null);
    setLastPayment(null);
    // Reset form
    setSelectedSlots([]);
    setCustomerData({ customer_name: '', customer_phone: '', customer_address: '' });
    loadSlots();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logout berhasil');
    navigate('/kasir/login');
  };

  const dates = Array.from({ length: 7 }, (_, i) => addDays(new Date(), i));

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlots([]);
    setShowCalendar(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-secondary rounded-md flex items-center justify-center">
                <span className="text-white font-black text-xl">K</span>
              </div>
              <span className="text-2xl font-heading font-black italic text-secondary uppercase">Kasir Panel</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="kasir-logout-btn"
              className="text-zinc-400 hover:text-red-500 font-medium transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8">
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white mb-2">Input Booking Walk-in</h1>
            <p className="text-zinc-400">Buat booking untuk customer yang datang langsung</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Venue Selection */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white uppercase font-heading mb-4">Pilih Lapangan</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {venues.map((venue) => (
                    <button
                      key={venue.id}
                      data-testid={`kasir-venue-${venue.id}`}
                      onClick={() => {
                        setSelectedVenue(venue);
                        setSelectedSlots([]);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedVenue?.id === venue.id
                          ? 'bg-secondary/10 border-secondary text-secondary'
                          : 'bg-zinc-800 border-zinc-700 text-white hover:border-secondary/50'
                      }`}
                    >
                      <div className="font-bold text-lg">{venue.name}</div>
                      <div className="text-sm opacity-70">{venue.type}</div>
                      <div className="text-lg font-black mt-2">
                        {Number(venue.price_per_hour) > 0 ? (
                          <>Rp {venue.price_per_hour.toLocaleString('id-ID')}/jam</>
                        ) : (
                          <span className="text-sm font-bold uppercase tracking-wider opacity-80">Mengikuti aturan</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Selection */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-6 h-6 text-secondary" />
                    <h2 className="text-xl font-bold text-white uppercase font-heading">Pilih Tanggal</h2>
                  </div>
                  <button
                    onClick={() => setShowCalendar(!showCalendar)}
                    className="text-secondary hover:text-secondary-hover font-semibold text-sm flex items-center gap-2"
                  >
                    {showCalendar ? 'Tutup Kalender' : 'Buka Kalender'}
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 mb-4">
                  {dates.map((date) => {
                    const isSelected = format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                    return (
                      <button
                        key={date.toString()}
                        onClick={() => handleDateSelect(date)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'bg-secondary border-secondary text-white'
                            : 'bg-zinc-800 border-zinc-700 text-white hover:border-secondary/50'
                        }`}
                      >
                        <div className="text-xs font-semibold">{format(date, 'EEE', { locale: idLocale })}</div>
                        <div className="text-lg font-bold">{format(date, 'd')}</div>
                        <div className="text-xs">{format(date, 'MMM', { locale: idLocale })}</div>
                      </button>
                    );
                  })}
                </div>

                {showCalendar && (
                  <div className="border-t border-zinc-700 pt-4">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => isBefore(date, startOfToday())}
                      className="rounded-md border border-zinc-700"
                    />
                  </div>
                )}
              </div>

              {/* Time Slots */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-secondary" />
                    <div>
                      <h2 className="text-xl font-bold text-white uppercase font-heading">Pilih Jam</h2>
                      <p className="text-sm text-zinc-400">Bisa pilih lebih dari 1 jam</p>
                    </div>
                  </div>
                  {selectedSlots.length > 0 && (
                    <div className="bg-secondary/10 border border-secondary/20 px-3 py-1 rounded-full">
                      <span className="text-secondary font-bold">{selectedSlots.length} jam</span>
                    </div>
                  )}
                </div>
                <div className="slot-grid">
                  {slots.map((slot) => {
                    const isSelected = selectedSlots.includes(slot.time);
                    return (
                      <button
                        key={slot.time}
                        data-testid={`kasir-slot-${slot.time}`}
                        onClick={() => {
                          if (slot.available) {
                            if (isSelected) {
                              setSelectedSlots(selectedSlots.filter(s => s !== slot.time));
                            } else {
                              setSelectedSlots([...selectedSlots, slot.time]);
                            }
                          }
                        }}
                        disabled={!slot.available}
                        className={`p-4 rounded-lg border-2 font-bold transition-all relative ${
                          !slot.available
                            ? 'bg-zinc-800 border-zinc-700 text-zinc-500 cursor-not-allowed'
                            : isSelected
                            ? 'bg-secondary border-secondary text-white'
                            : 'bg-zinc-800 border-zinc-700 text-white hover:border-secondary/50'
                        }`}
                      >
                        <div className="text-sm">{slot.time}</div>
                        {!slot.available && <div className="text-xs mt-1">Penuh</div>}
                        {isSelected && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                            <span className="text-secondary text-xs">✓</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Customer Data */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white uppercase font-heading mb-6">Data Customer</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">Nama Customer *</label>
                    <input
                      data-testid="kasir-customer-name"
                      type="text"
                      required
                      value={customerData.customer_name}
                      onChange={(e) => setCustomerData({ ...customerData, customer_name: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                      placeholder="Nama lengkap customer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">No. Handphone *</label>
                    <input
                      data-testid="kasir-customer-phone"
                      type="tel"
                      required
                      value={customerData.customer_phone}
                      onChange={(e) => setCustomerData({ ...customerData, customer_phone: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                      placeholder="08123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">Alamat (Opsional)</label>
                    <textarea
                      data-testid="kasir-customer-address"
                      value={customerData.customer_address}
                      onChange={(e) => setCustomerData({ ...customerData, customer_address: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary text-white rounded-md px-4 py-3 placeholder:text-zinc-600 transition-all outline-none resize-none"
                      placeholder="Alamat customer"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Status */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white uppercase font-heading mb-4">Status Pembayaran</h2>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    data-testid="payment-cash"
                    onClick={() => setPaymentStatus('cash')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentStatus === 'cash'
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-zinc-800 border-zinc-700 text-white hover:border-primary/50'
                    }`}
                  >
                    <div className="font-bold">Bayar Tunai</div>
                    <div className="text-xs mt-1 opacity-70">Sudah dibayar di kasir</div>
                  </button>
                  <button
                    data-testid="payment-pending"
                    onClick={() => setPaymentStatus('pending')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentStatus === 'pending'
                        ? 'bg-secondary/10 border-secondary text-secondary'
                        : 'bg-zinc-800 border-zinc-700 text-white hover:border-secondary/50'
                    }`}
                  >
                    <div className="font-bold">Belum Bayar</div>
                    <div className="text-xs mt-1 opacity-70">Booking dulu, bayar nanti</div>
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 sticky top-24">
                <h2 className="text-xl font-bold text-white uppercase font-heading mb-6">Ringkasan</h2>
                <div className="space-y-4 mb-6">
                  <div>
                    <div className="text-sm text-zinc-400">Lapangan</div>
                    <div className="text-white font-bold">{selectedVenue?.name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-zinc-400">Tanggal</div>
                    <div className="text-white font-bold">
                      {format(selectedDate, 'dd MMMM yyyy', { locale: idLocale })}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-zinc-400">Jam Dipilih</div>
                    <div className="text-white font-bold">
                      {selectedSlots.length === 0 ? '-' : `${selectedSlots.length} slot`}
                    </div>
                    {selectedSlots.length > 0 && (
                      <div className="text-xs text-zinc-500 mt-1">
                        {selectedSlots.sort().join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="border-t border-zinc-700 pt-4">
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-sm text-zinc-400">Harga per jam</div>
                      <div className="text-white font-bold">
                        {(() => {
                          const bookingDateStr = format(selectedDate, 'yyyy-MM-dd');
                          const totalAmount = calculateTotalAmount(selectedSlots, bookingDateStr);
                          if (selectedSlots.length === 0) return 'Rp -';
                          if (totalAmount == null || totalAmount <= 0) return 'Rp -';
                          return `Rp ${totalAmount.toLocaleString('id-ID')}`;
                        })()}
                      </div>
                    </div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-sm text-zinc-400">Jumlah jam</div>
                      <div className="text-white font-bold">{selectedSlots.length} jam</div>
                    </div>
                    <div className="text-sm text-zinc-400 mb-1">Total Harga</div>
                    <div className="text-2xl font-black text-secondary">
                      {(() => {
                        const bookingDateStr = format(selectedDate, 'yyyy-MM-dd');
                        const totalAmount = calculateTotalAmount(selectedSlots, bookingDateStr);
                        if (selectedSlots.length === 0) return 'Rp -';
                        if (totalAmount == null || totalAmount <= 0) return 'Rp -';
                        return `Rp ${totalAmount.toLocaleString('id-ID')}`;
                      })()}
                    </div>
                  </div>
                  <div className="border-t border-zinc-700 pt-4">
                    <div className="text-sm text-zinc-400 mb-1">Status Pembayaran</div>
                    <div className="font-bold text-white">
                      {paymentStatus === 'cash' ? '💵 Tunai (Lunas)' : '⏳ Belum Bayar'}
                    </div>
                  </div>
                </div>
                <button
                  data-testid="kasir-submit-booking"
                  onClick={handleBooking}
                  disabled={submitting || selectedSlots.length === 0 || !selectedVenue}
                  className="w-full bg-secondary text-white hover:bg-secondary-hover rounded-none skew-btn px-8 py-3 font-bold uppercase tracking-wider transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span>{submitting ? 'Memproses...' : 'Buat Booking'}</span>
                  {!submitting && <Plus className="w-5 h-5" />}
                </button>
                
                <Link
                  to="/kasir/dashboard"
                  className="w-full mt-4 border-2 border-zinc-700 text-zinc-400 hover:border-secondary hover:text-secondary rounded-lg px-6 py-2 font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <Home className="w-4 h-4" />
                  Dashboard Kasir
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Receipt Modal */}
      {showReceipt && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-2xl font-heading font-bold text-white uppercase mb-4 text-center">
              Booking Berhasil!
            </h2>
            
            {/* Receipt Preview */}
            <div className="flex justify-center mb-6 overflow-auto">
              <Receipt 
                ref={receiptRef}
                booking={lastBooking}
                venue={selectedVenue}
                payment={lastPayment}
                totalAmount={lastPayment?.amount ?? calculateTotalAmount(lastBooking?.time_slots, lastBooking?.booking_date)}
              />
            </div>
            
            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handlePrint}
                data-testid="print-receipt-btn"
                className="w-full bg-primary text-black hover:bg-primary-hover rounded-lg px-4 py-3 font-bold uppercase flex items-center justify-center gap-2 transition-all"
              >
                <Printer className="w-5 h-5" />
                Cetak Struk
              </button>
              <button
                onClick={() => {
                  handleCloseReceipt();
                  navigate('/kasir/dashboard');
                }}
                data-testid="go-dashboard-btn"
                className="w-full bg-secondary text-white hover:bg-secondary-hover rounded-lg px-4 py-3 font-bold uppercase transition-all flex items-center justify-center gap-2"
              >
                <Home className="w-5 h-5" />
                Dashboard
              </button>
              <button
                onClick={handleCloseReceipt}
                data-testid="close-receipt-btn"
                className="w-full bg-zinc-700 text-white hover:bg-zinc-600 rounded-lg px-4 py-3 font-bold uppercase transition-all"
              >
                Selesai
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default KasirBooking;
